import { Input, OnDestroy, Output } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { RenderingContext, RenderingContextProvider } from './../../../api';
import {
  createSingleSubject,
  DEFAULT_LAYOUT_MODE,
  KEY_LAYOUT_MODE,
  KEY_RENDERING_CONTEXT,
  opBoxLayoutMode,
  opDistinctUntilChanged,
  opFilterNotNil,
  opShareLast,
  perfMeasure,
  rxPipe,
} from './../../../utils';
import { li } from './../../services/logger/logger.service';
import { assign, defineProperties, getClassName, KEY_DEBUG } from './../../utils/js.utils';
import { AbstractLifeCycleComponent, createGetter, createSetter } from './abstract.lifecycle.component';

/* Copyright IBM Corp. 2017 */
const LOGGER = 'AbstractRenderingComponent';

let ID = 0;

/*
 * Convenience base class for components that work with a {@link RenderingContext}.
 */
export abstract class AbstractRenderingComponent
  extends AbstractLifeCycleComponent
  implements OnDestroy, RenderingContextProvider {
  /* the ID of the component, can be used for debugging purposes */
  protected readonly _id: string;

  /*
   * Returns an Observable for the rendering context
   */
  @Output() readonly onRenderingContext: Observable<RenderingContext>;

  /*
   * Returns an Observable for the layout mode
   */
  @Output() readonly onLayoutMode: Observable<string>;

  /**
   * The current rendering context for convenience
   */
  @Input() renderingContext: RenderingContext;

  /**
   * The current layout mode for convenience
   */
  @Input() layoutMode: string;

  /*
   * Our constructor
   */
  constructor() {
    super();
    // measure startup
    const handle = perfMeasure(LOGGER);
    // our own pointer
    const that = this;

    // somehow identify this component
    const componentId = (this._id = `${getClassName(that, LOGGER)}[${ID++}]`);

    /**
     * Make this a function so we can add a breakpoint if required
     *
     * @param aRenderingContext     the context
     */
    function _logRenderingContext(aRenderingContext: RenderingContext) {
      // some logging
      li(
        LOGGER,
        'onRenderingContext',
        componentId,
        aRenderingContext[KEY_DEBUG]
      );
    }

    // init the subject
    const opUntilDestroyed: {
      <T>(src: Observable<T>): Observable<T>;
    } = takeUntil(that.onOnDestroy);

    const rcSubject = createSingleSubject<RenderingContext>();
    const modeSubject = new BehaviorSubject<string>(DEFAULT_LAYOUT_MODE);

    // rendering context, until the class gets destroyed
    const onRenderingContext: Observable<RenderingContext> = rxPipe(
      rcSubject,
      opFilterNotNil,
      opDistinctUntilChanged,
      tap(_logRenderingContext, undefined, () =>
        li(LOGGER, 'onRenderingContextComplete', componentId)
      ),
      opShareLast
    );

    // define the setters
    const onLayoutMode: Observable<string> = rxPipe(
      modeSubject,
      opBoxLayoutMode,
      opDistinctUntilChanged,
      tap(
        mode => li(LOGGER, 'onLayoutMode', mode, componentId),
        undefined,
        () => li(LOGGER, 'onLayoutModeComplete', componentId)
      ),
      opShareLast
    );

    // define getters and setters
    defineProperties(that, {
      [KEY_RENDERING_CONTEXT]: assign(
        createGetter(onRenderingContext, that),
        createSetter(rcSubject, that)
      ),
      [KEY_LAYOUT_MODE]: assign(
        createGetter(onLayoutMode, that),
        createSetter(modeSubject, that)
      )
    });

    // the handlers
    this.onRenderingContext = rxPipe(onRenderingContext, opUntilDestroyed);
    this.onLayoutMode = rxPipe(onLayoutMode, opUntilDestroyed);

    // done
    handle();
  }

  /*
   * Returns an Observable for the rendering context
   *
   * @deprecated use onRenderingContext instead
   */
  protected get context(): Observable<RenderingContext> {
    // represent the subject as an observable
    return this.onRenderingContext;
  }

  /*
   * Generates a unique ID for the rendering context
   *
   * @param aIndex index of the iteration
   * @param aRenderingContext the rendering context
   * @return the unique ID
   */
  trackByComponentId(
    aIndex: number,
    aRenderingContext: RenderingContext
  ): string {
    // uses the component ID to track the identity
    return aRenderingContext.id;
  }

  // just dispatch
  ngOnDestroy() {
    super.ngOnDestroy();
  }
}
